import {McpServerDialog} from '@github-ui/configure-mcp-server-partial/McpServerDialog'
import {PlusIcon, SearchIcon, ToolsIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Spinner, TextInput, TreeView} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import React, {useCallback, useEffect, useState} from 'react'

import styles from './McpToolsManager.module.css'

// Types for the API response
interface McpTool {
  id: string
  text: string
}

interface McpServer {
  id: string
  text: string
  display_name?: string
  tools: McpTool[]
}

interface McpServersResponse {
  servers: McpServer[]
  total_count: number
  page: number
  total_pages: number
}

interface McpToolsManagerProps {
  anchorRef: React.RefObject<HTMLButtonElement>
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectionChange?: (selectedServers: Array<{serverId: string; serverText: string}>) => void
}

export const McpToolsManager: React.FC<McpToolsManagerProps> = ({anchorRef, open, onOpenChange, onSelectionChange}) => {
  const [showAddMcpDialog, setShowAddMcpDialog] = useState(false)
  const [servers, setServers] = useState<McpServer[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedItems, setSelectedItems] = useState<Set<string>>(() => new Set())
  const [filter, setFilter] = useState('')

  // Selection handlers
  const handleServerSelect = (serverId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(serverId)) {
        newSet.delete(serverId)
      } else {
        newSet.add(serverId)
      }
      return newSet
    })
  }

  // Get selected servers for external use
  const getSelectedServers = useCallback(() => {
    const selectedServers: Array<{serverId: string; serverText: string}> = []

    for (const server of servers) {
      if (selectedItems.has(server.id)) {
        selectedServers.push({
          serverId: server.id,
          serverText: server.text,
        })
      }
    }

    return selectedServers
  }, [servers, selectedItems])

  // Track selected servers for external use
  React.useEffect(() => {
    const selectedServers = getSelectedServers()
    onSelectionChange?.(selectedServers)
  }, [selectedItems, servers, getSelectedServers, onSelectionChange])

  // Fetch MCP servers when the panel opens
  useEffect(() => {
    if (open) {
      void fetchMcpServers()
    }
  }, [open])

  const fetchMcpServers = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/copilot/mcp/servers.json', {
        headers: {
          Accept: 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data: McpServersResponse = await response.json()
      setServers(data.servers)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load MCP servers')
    } finally {
      setLoading(false)
    }
  }

  const handleAddMcpClick = () => {
    setShowAddMcpDialog(true)
  }

  const handleDialogClose = () => {
    setShowAddMcpDialog(false)
  }

  const handleServerAdded = () => {
    // Refresh the server list when a new server is added
    if (open) {
      void fetchMcpServers()
    }
  }

  return (
    <>
      <AnchoredOverlay
        key="mcp-tool-manager"
        open={open}
        onOpen={() => onOpenChange(true)}
        onClose={() => onOpenChange(false)}
        anchorRef={anchorRef}
        renderAnchor={() => <></>}
        width="medium"
      >
        <div className={styles.container}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerTitle}>MCP servers</div>
            <TextInput
              placeholder="Search by provider"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className={styles.searchInput}
              leadingVisual={SearchIcon}
            />
          </div>

          {/* Content */}
          <div className={styles.contentArea}>
            {loading ? (
              <div className={styles.loadingContainer}>
                <Spinner />
              </div>
            ) : error ? (
              <Blankslate>
                <Blankslate.Visual>⚠️</Blankslate.Visual>
                <Blankslate.Heading>Error loading tools</Blankslate.Heading>
              </Blankslate>
            ) : servers.length === 0 ? (
              <div className={styles.emptyState}>
                <ToolsIcon size={24} />
                <div className={styles.emptyStateTitle}>You don&apos;t have any MCP tools configured</div>
                <div className={styles.emptyStateDescription}>
                  Add one to connect models to files, APIs, databases, and more.
                </div>
              </div>
            ) : (
              <TreeView aria-label="MCP server selection" aria-multiselectable="true">
                {servers
                  .filter(server => server.text.toLowerCase().includes(filter.toLowerCase()))
                  .map(server => {
                    const isSelected = selectedItems.has(server.id)

                    return (
                      <TreeView.Item
                        key={server.id}
                        id={server.id}
                        onSelect={() => handleServerSelect(server.id)}
                        current={isSelected}
                        aria-selected={isSelected}
                      >
                        <TreeView.LeadingVisual>
                          {/* TODO: Add checkbox as it was removed for staff-ship */}
                          <span className={styles.serverName}>{server.display_name || server.text}</span>
                        </TreeView.LeadingVisual>

                        <TreeView.SubTree>
                          <TreeView.Item id={`${server.id}-tool-progress`}>{'Tool list in progress'}</TreeView.Item>
                        </TreeView.SubTree>
                      </TreeView.Item>
                    )
                  })}
              </TreeView>
            )}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <Button variant="default" leadingVisual={PlusIcon} onClick={handleAddMcpClick} block>
              New tool
            </Button>
          </div>
        </div>
      </AnchoredOverlay>

      <McpServerDialog isOpen={showAddMcpDialog} onClose={handleDialogClose} onServerAdded={handleServerAdded} />
    </>
  )
}

try{ McpToolsManager.displayName ||= 'McpToolsManager' } catch {}